import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient, type ApDemoOverview, type ApDemoPayment } from '../lib/api-client';
import { ApCasePack } from '../components/ap/ApCasePack';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

export function PaymentsPage() {
  const [params] = useSearchParams();
  const caseId = params.get('caseId');
  const [overview, setOverview] = useState<ApDemoOverview | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [confirmPay, setConfirmPay] = useState(false);

  const lines = overview?.payments ?? [];
  const payableCount = (overview?.cases ?? []).filter((row) => row.status === 'IN_PAYMENT_PROPOSAL').length;
  const selectedLine = useMemo(
    () => lines.find((line) => line.payment_line_id === selectedId) ?? lines[0] ?? null,
    [lines, selectedId],
  );
  const selectedCase = useMemo(() => {
    if (!selectedLine) return null;
    return overview?.cases.find((row) => row.case_id === selectedLine.case_id) ?? null;
  }, [overview, selectedLine]);
  const selectedAudit = useMemo(() => {
    if (!selectedCase) return [];
    return (overview?.audit ?? []).filter((entry) => entry.case_id === selectedCase.case_id);
  }, [overview, selectedCase]);

  useEffect(() => {
    void apiClient.getApDemoOverview().then((next) => {
      setOverview(next);
      if (caseId) {
        const line = next.payments.find((row) => row.case_id === caseId);
        if (line) setSelectedId(line.payment_line_id);
      } else if (next.payments[0]) {
        setSelectedId(next.payments[0].payment_line_id);
      }
    }).catch(() => setOverview(null));
  }, [caseId]);

  function caseOf(id: string) {
    return overview?.cases.find((row) => row.case_id === id);
  }

  function select(line: ApDemoPayment) {
    setSelectedId(line.payment_line_id);
  }

  async function pay() {
    setPaying(true);
    try {
      const res = await apiClient.payApRun();
      setOverview(res.overview);
    } finally {
      setPaying(false);
      setConfirmPay(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Payment orders</h1>
          <p className="text-sm text-gray-500">Illustrative treasury board. Stands in for Halcom / the payment system. No bank execution. The agent never pays.</p>
        </div>
        <Link to="/mailbox" data-testid="payments-to-mailbox"><Button size="sm" variant="ghost">Mailbox</Button></Link>
      </div>
      {!overview?.packEnabled ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-gray-700 dark:bg-gray-900">
          Enable the AP pack from Mailbox, then drop an invoice. Payment orders stay empty until a case reaches proposal.
        </div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="payment-orders-table">
              <thead>
                <tr className="text-xs uppercase text-gray-400">
                  <th className="py-2">Invoice</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>IBAN</th>
                  <th>Due</th>
                  <th>Case status</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 && (
                  <tr><td colSpan={6} className="py-3 text-gray-400">No payment orders. Drop a straight-through invoice from Mailbox.</td></tr>
                )}
                {lines.map((line) => (
                  <tr
                    key={line.payment_line_id}
                    className={`cursor-pointer border-t border-gray-100 dark:border-gray-800 ${selectedId === line.payment_line_id ? 'bg-blue-50 dark:bg-blue-950/40' : ''}`}
                    onClick={() => select(line)}
                    data-testid={`payment-row-${line.payment_line_id}`}
                  >
                    <td className="py-2">{line.invoice_number}</td>
                    <td>{line.vendor_name}</td>
                    <td>{line.amount} {line.currency}</td>
                    <td className="font-mono text-xs">{line.iban}</td>
                    <td>{line.due_date}</td>
                    <td>{caseOf(line.case_id)?.status ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {selectedCase && <div className="mt-5"><ApCasePack caseRow={selectedCase} audit={selectedAudit} /></div>}
          {payableCount > 0 && (
            <div className="mt-4">
              <Button type="button" className="bg-green-600 hover:bg-green-700" disabled={paying} data-testid="treasury-pay" onClick={() => setConfirmPay(true)}>Pay</Button>
              <p className="mt-2 text-xs text-gray-500">Approves the mock payment proposal in OrqForge. No funds move. History stays on this page.</p>
            </div>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirmPay}
        message="Approve this mock payment proposal? No bank execution."
        confirmText="Pay"
        onCancel={() => setConfirmPay(false)}
        onConfirm={() => void pay()}
      />
    </div>
  );
}
